class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.errorMessage = this.querySelector('[data-product-form-error]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    if (!this.form) return;

    this.form.removeEventListener('submit', this.onSubmit);
  }

  async onSubmit(event) {
    if (event.submitter?.name === 'checkout') return;

    event.preventDefault();

    if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

    this.setLoading(true);
    this.clearError();

    try {
      const addonItems = this.getAddonItems();
      const parsedState = addonItems
        ? await this.submitItems(addonItems)
        : await this.submitSingleItem();

      if (!parsedState) return;

      this.dispatchEvent(
        new CustomEvent('product-form:added', {
          bubbles: true,
          detail: {
            productForm: this,
            cartItem: parsedState,
          },
        })
      );

      if (!this.cart) {
        window.location = this.getCartUrl();
      }
    } catch (error) {
      this.handleError({ description: error.message });
    } finally {
      this.setLoading(false);
    }
  }

  async submitSingleItem() {
    const response = await fetch(this.getCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: new FormData(this.form),
    });

    const parsedState = await response.json();

    if (!response.ok || parsedState.status) {
      this.handleError(parsedState);
      return null;
    }

    return parsedState;
  }

  getAddonConfig() {
    const configElement = this.querySelector('[data-product-addon-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      return null;
    }
  }

  getAddonItems() {
    const config = this.getAddonConfig();
    if (!config || !Array.isArray(config.items) || config.items.length === 0) return null;

    try {
      return config.items
        .map((item) => ({
          id: Number(item.id),
          quantity: Number(item.quantity) || 1,
          selling_plan: item.selling_plan ? Number(item.selling_plan) : null,
        }))
        .filter((item) => item.id > 0);
    } catch (error) {
      return null;
    }
  }

  createBundleId() {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.floor(Math.random() * 16);
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  isSubscribePlanSelected() {
    if (this.dataset.defaultSubscribe === 'true') {
      return true;
    }

    return (
      this.closest('.product-information')?.querySelector('.product-subscription__input:checked')?.value ===
      'subscribe'
    );
  }

  async submitItems(items) {
    const config = this.getAddonConfig();
    const bundleName = config?.bundle_name;
    const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
    const formSellingPlanId = sellingPlanInput?.value ? Number(sellingPlanInput.value) : null;
    const parentItem = items[items.length - 1];
    const bundleSellingPlan = this.isSubscribePlanSelected()
      ? parentItem?.selling_plan || formSellingPlanId
      : null;
    const bundleId = this.createBundleId();

    const payloadItems = items.map((item, index) => {
      const isBundleParent = index === items.length - 1;
      const payload = {
        id: item.id,
        quantity: item.quantity,
        properties: {
          _bundle_id: bundleId,
        },
      };

      if (isBundleParent) {
        payload.properties._bundle_parent = true;
      }

      if (isBundleParent && bundleName) {
        payload.properties._bundle_name = bundleName;
      }

      if (bundleSellingPlan) {
        payload.selling_plan = bundleSellingPlan;
      }

      return payload;
    });

    const response = await fetch(this.getCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: payloadItems }),
    });

    const parsedState = await response.json();

    if (!response.ok || parsedState.status) {
      this.handleError(parsedState);
      return null;
    }

    return parsedState;
  }

  getCartAddUrl() {
    const formAction = this.form.getAttribute('action') || '/cart/add';
    const url = new URL(formAction, window.location.origin);

    if (!url.pathname.endsWith('.js')) {
      url.pathname = url.pathname.replace(/\/cart\/add\/?$/, '/cart/add.js');
    }

    return url.toString();
  }

  getCartUrl() {
    const url = new URL(this.form.getAttribute('action') || '/cart/add', window.location.origin);
    url.pathname = url.pathname.replace(/\/cart\/add\/?$/, '/cart');

    return url.toString();
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;

    this.submitButton.setAttribute('aria-disabled', isLoading.toString());
    this.submitButton.classList.toggle('is-loading', isLoading);
  }

  clearError() {
    if (!this.errorMessage) return;

    this.errorMessage.hidden = true;
    this.errorMessage.textContent = '';
  }

  handleError(state) {
    const message = state.description || state.message || 'Unable to add item to cart.';

    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    this.dispatchEvent(
      new CustomEvent('product-form:error', {
        bubbles: true,
        detail: {
          productForm: this,
          error: state,
        },
      })
    );
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
